import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { useMemo, useCallback } from "react";
import { useStoreState } from "pullstate";
import ACRStore from "../../../utils/ACRStore";
import { evaluateConditionalRules } from "../utils/conditionalRuleEvaluator";
import { evaluateCrossFieldRules } from "../utils/crossFieldValidator";
import { evaluateSpecialChecks } from "../utils/specialChecksValidator";
import { validateAllFields } from "../utils/fieldValidator";
import { isEmptyValue } from "../../../utils/formValueUtils";
import { useValidationErrors } from "./useValidationErrors";
/**
 * Centralized Error Manager Hook
 *
 * Single source of truth for all error management:
 * - Fetches backend errors from store and ACR validation errors from API
 * - Automatically filters out stale backend errors for changed fields
 * - Provides different error views for different purposes (badge, submit, field display)
 * - Handles error clearing when fields change
 */
export var useErrorManager = function useErrorManager(_ref) {
  var activeSubmissionID = _ref.activeSubmissionID,
    submissionID = _ref.submissionID,
    acrValidationErrors = _ref.acrValidationErrors,
    submissionDetail = _ref.submissionDetail,
    formData = _ref.formData,
    fieldSchemas = _ref.fieldSchemas,
    crossFieldRules = _ref.crossFieldRules,
    registryTaggedFields = _ref.registryTaggedFields;
  // Fetch server field errors from store
  var serverFieldErrors = useStoreState(ACRStore, function (s) {
    return s.fieldErrors[activeSubmissionID || 0];
  }, [activeSubmissionID]);

  // Fetch full field errors from store
  var fullFieldErrors = useStoreState(ACRStore, function (s) {
    return s.fullFieldErrors[activeSubmissionID || 0];
  }, [activeSubmissionID]);

  // Get validation errors from ACR API response
  var validationFieldErrors = useValidationErrors(acrValidationErrors || null);
  // Get changed fields set from store
  var changedFieldsSet = useStoreState(ACRStore, function (s) {
    var _s$submissionChangedF;
    return ((_s$submissionChangedF = s.submissionChangedFields) === null || _s$submissionChangedF === void 0 || (_s$submissionChangedF = _s$submissionChangedF[(submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id) || ""]) === null || _s$submissionChangedF === void 0 ? void 0 : _s$submissionChangedF[(submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.id) || 0]) || new Set();
  }, [submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.patient_id, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.id]);

  // Calculate frontend conditional errors (always fresh, based on current form data)
  var frontendConditionalErrors = useMemo(function () {
    return evaluateConditionalRules(formData || {}, fieldSchemas);
  }, [formData, fieldSchemas]);

  // Calculate cross-field validation errors (always fresh)
  var crossFieldErrors = useMemo(function () {
    if (!formData || !crossFieldRules || crossFieldRules.length === 0) {
      return [];
    }
    return evaluateCrossFieldRules(formData, crossFieldRules, fieldSchemas);
  }, [formData, crossFieldRules, fieldSchemas]);

  // Calculate special check errors (always fresh)
  var specialCheckErrors = useMemo(function () {
    if (!formData || !registryTaggedFields) {
      return [];
    }
    return evaluateSpecialChecks(formData, registryTaggedFields, fieldSchemas);
  }, [formData, registryTaggedFields, fieldSchemas]);

  // Calculate frontend field validation errors (pattern, format_hint, min/max) - always fresh
  var frontendValidationErrors = useMemo(function () {
    if (!formData || !fieldSchemas) {
      return [];
    }
    return validateAllFields(formData, fieldSchemas);
  }, [formData, fieldSchemas]);

  // Convert ACR validation errors to FieldError[] format
  var acrValidationFieldErrors = useMemo(function () {
    return Object.entries(validationFieldErrors).map(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
        fieldName = _ref3[0],
        errorMessage = _ref3[1];
      return {
        field: fieldName,
        error: errorMessage,
        type: "validation"
      };
    });
  }, [validationFieldErrors]);

  /**
   * Merge all errors: backend errors for unchanged fields, fresh frontend errors for all fields.
   * When a field changes, stale backend errors are skipped and fresh frontend errors take over.
   *
   * Error priority order (for display, last wins):
   * 1. Frontend Validation Errors (pattern, format_hint, min/max)
   * 2. Cross-Field Errors
   * 3. Frontend Conditional Errors
   * 4. Special Check Errors
   * 5. Backend Errors (filtered for staleness)
   * 6. ACR Validation Errors (highest priority)
   *
   * Logic: If a field has validation errors (format/pattern), skip conditional/special checks
   * for that field. Fix format first, then check business rules.
   */
  var fieldErrorsList = useMemo(function () {
    var mergedErrors = [];
    var fieldsWithValidationErrors = new Set();

    // 1. Add frontend validation errors (pattern, format_hint, min/max) - always fresh
    frontendValidationErrors.forEach(function (error) {
      mergedErrors.push(error);
      fieldsWithValidationErrors.add(error.field);
    });

    // 2. Add cross-field errors (always fresh)
    // Note: Cross-field errors are kept even if a field has validation errors,
    // as they describe relationships between fields
    crossFieldErrors.forEach(function (error) {
      mergedErrors.push(error);
    });

    // 3. Add frontend conditional errors (always fresh)
    // Skip if field already has validation error
    frontendConditionalErrors.forEach(function (error) {
      if (!fieldsWithValidationErrors.has(error.field)) {
        mergedErrors.push(error);
      }
    });

    // 4. Add special check errors (always fresh)
    // Skip if field already has validation error
    specialCheckErrors.forEach(function (error) {
      if (!fieldsWithValidationErrors.has(error.field)) {
        mergedErrors.push(error);
      }
    });

    // 5. Add backend errors (fullFieldErrors) with filtering
    (fullFieldErrors || []).forEach(function (backendError) {
      var fieldWasChanged = changedFieldsSet === null || changedFieldsSet === void 0 ? void 0 : changedFieldsSet.has(backendError.field);

      // Skip backend conditional errors (frontend always recalculates fresh)
      if (backendError.type === "conditional") {
        return;
      }

      // Skip backend required errors if field was changed and is now populated
      if (backendError.type === "required" && fieldWasChanged) {
        var fieldValue = formData === null || formData === void 0 ? void 0 : formData[backendError.field];
        if (!isEmptyValue(fieldValue)) {
          return;
        }
      }

      // Skip backend validation errors if field was changed (frontend provides fresh)
      if (backendError.type === "validation" && fieldWasChanged) {
        return;
      }

      // Skip backend special_check and cross_field errors if ANY field changed
      // These error types involve multiple fields, so if any field changed since last save,
      // the frontend recalculates fresh errors and backend errors are stale
      if ((backendError.type === "special_check" || backendError.type === "cross_field") && changedFieldsSet.size > 0) {
        return;
      }

      // Skip special check backend errors if field has validation error
      if (fieldsWithValidationErrors.has(backendError.field) && backendError.type === "special_check") {
        return;
      }
      mergedErrors.push(backendError);
    });

    // 6. Add ACR validation errors (highest priority for display)
    acrValidationFieldErrors.forEach(function (error) {
      var fieldWasChanged = changedFieldsSet === null || changedFieldsSet === void 0 ? void 0 : changedFieldsSet.has(error.field);
      // Skip if field was changed (frontend validation handles it)
      if (!fieldWasChanged) {
        mergedErrors.push(error);
      }
    });
    return mergedErrors;
  }, [fullFieldErrors, acrValidationFieldErrors, frontendConditionalErrors, frontendValidationErrors, crossFieldErrors, specialCheckErrors, changedFieldsSet, formData]);

  // Filter errors for badge: blocking errors only, exclude required markers and optional warnings
  var errorsForBadge = useMemo(function () {
    return fieldErrorsList.filter(function (error) {
      var _error$conditional_me, _error$conditional_me2;
      // Must have an error message
      if (!error.error || error.error.trim() === "") return false;

      // Exclude conditional required markers (just for asterisks, not blocking)
      // Keep special check required errors (have messages and should block submission)
      if (error.type === "conditional" && ((_error$conditional_me = error.conditional_metadata) === null || _error$conditional_me === void 0 ? void 0 : _error$conditional_me.type) === "required") {
        return false;
      }

      // Exclude conditional optional warnings (non-blocking, informational)
      // Keep special_check/cross_field optional errors (these enforce constraints on populated values)
      if (error.type === "conditional" && ((_error$conditional_me2 = error.conditional_metadata) === null || _error$conditional_me2 === void 0 ? void 0 : _error$conditional_me2.type) === "optional") {
        return false;
      }
      return true;
    });
  }, [fieldErrorsList]);

  /**
   * Get errors for submit validation
   * Same as errorsForBadge (blocking errors only)
   */
  var errorsForSubmit = errorsForBadge;

  /**
   * Check if field has any error
   */
  var hasFieldError = useCallback(function (fieldName) {
    return fieldErrorsList.some(function (error) {
      return error.field === fieldName;
    });
  }, [fieldErrorsList]);

  /**
   * Get error for a specific field
   * Returns the last error (highest priority) for the field
   */
  var getFieldError = useCallback(function (fieldName) {
    // Find all errors for this field and return the last one (highest priority)
    var errors = fieldErrorsList.filter(function (error) {
      return error.field === fieldName;
    });
    return errors.length > 0 ? errors[errors.length - 1] : undefined;
  }, [fieldErrorsList]);

  /**
   * Get all errors for a specific field (there might be multiple)
   */
  var getFieldErrors = useCallback(function (fieldName) {
    return fieldErrorsList.filter(function (error) {
      return error.field === fieldName;
    });
  }, [fieldErrorsList]);

  /**
   * Check if all required fields are completed
   * Checks multiple sources:
   * 1. Fields with FieldError where type/conditional_metadata.type === "required"
   * 2. Fields with is_acr_required === true in schema
   * 3. Fields with visibility === "required" in schema
   * 4. Fields in submissionDetail.missing_fields
   */
  var allRequiredFieldsCompleted = useMemo(function () {
    if (!formData || !fieldSchemas) return false;

    // 1. Check if there are any required field errors (fields that are required but empty)
    var hasRequiredFieldErrors = fieldErrorsList.some(function (error) {
      var _error$conditional_me3;
      var isRequiredError = error.type === "required" || ((_error$conditional_me3 = error.conditional_metadata) === null || _error$conditional_me3 === void 0 ? void 0 : _error$conditional_me3.type) === "required";
      if (!isRequiredError) return false;

      // Check if this required field is actually empty
      var fieldValue = formData[error.field];
      return isEmptyValue(fieldValue);
    });
    if (hasRequiredFieldErrors) return false;

    // 2. Check static schema required fields (is_acr_required or visibility === "required")
    var hasEmptyStaticRequiredFields = fieldSchemas.some(function (schema) {
      var isStaticRequired = schema.is_acr_required === true || schema.visibility === "required";
      if (!isStaticRequired) return false;
      var fieldValue = formData[schema.field_name];
      return isEmptyValue(fieldValue);
    });
    if (hasEmptyStaticRequiredFields) return false;

    // 3. Check backend missing_fields
    if (submissionDetail !== null && submissionDetail !== void 0 && submissionDetail.missing_fields && submissionDetail.missing_fields.length > 0) {
      var hasEmptyMissingFields = submissionDetail.missing_fields.some(function (fieldName) {
        var fieldValue = formData[fieldName];
        return isEmptyValue(fieldValue);
      });
      if (hasEmptyMissingFields) return false;
    }
    return true;
  }, [fieldErrorsList, formData, fieldSchemas, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.missing_fields]);

  // Build fieldErrors Record for backward compatibility with form fields
  // Last error wins (highest priority) for each field
  var fieldErrors = useMemo(function () {
    var errorMap = {};
    // Iterate through all errors, last one overwrites previous (highest priority)
    fieldErrorsList.forEach(function (error) {
      if (error.error) {
        errorMap[error.field] = error.error;
      }
    });
    return errorMap;
  }, [fieldErrorsList]);

  // Build allFullFieldErrors for SharedFormDataContext
  var allFullFieldErrors = useMemo(function () {
    return [].concat(_toConsumableArray(fullFieldErrors || []), _toConsumableArray(acrValidationFieldErrors), _toConsumableArray(crossFieldErrors), _toConsumableArray(specialCheckErrors));
  }, [fullFieldErrors, acrValidationFieldErrors, crossFieldErrors, specialCheckErrors]);
  return {
    // Error lists for different purposes
    fieldErrorsList: fieldErrorsList,
    // All errors (merged backend + frontend, stale filtered)
    fieldErrors: fieldErrors,
    // Record format for backward compatibility
    allFullFieldErrors: allFullFieldErrors,
    // All errors (backend + ACR validation + cross-field + special checks)
    errorsForBadge: errorsForBadge,
    // Blocking errors only (for badge display)
    errorsForSubmit: errorsForSubmit,
    // Blocking errors only (for submit validation)
    frontendConditionalErrors: frontendConditionalErrors,
    // Frontend conditional errors only (for debugging)
    frontendValidationErrors: frontendValidationErrors,
    // Frontend validation errors only (for debugging)

    // Error checking methods
    hasFieldError: hasFieldError,
    getFieldError: getFieldError,
    getFieldErrors: getFieldErrors,
    // Validation helpers
    allRequiredFieldsCompleted: allRequiredFieldsCompleted,
    hasBlockingErrors: errorsForSubmit.length > 0,
    // Fetched error sources (for context)
    serverFieldErrors: serverFieldErrors,
    validationFieldErrors: validationFieldErrors,
    // Metadata
    changedFieldsSet: changedFieldsSet
  };
};